import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { MapContainer, Marker, TileLayer, useMap } from 'react-leaflet';
import type { LatLngExpression } from 'leaflet';
import L from 'leaflet';
import { userLocation } from '../../../store/selectors/location';
import { useSelector } from 'react-redux';
// import { useAskTelegramLocation } from '../../../lib/useAskTelegramLocation';
import { Button } from '../../../components/ui/button';
import { LocationFooterIcon } from '../../../assets/footer/Location';
import { ArrowIcon } from '../../../assets/Arrow';
import {
  Drawer,
  DrawerContent,
  // DrawerOverlay,
  // DrawerPortal,
  DrawerTitle,
} from '../../../components/ui/drawer';

import { XIcon } from '../../../assets/X';
import { t } from 'i18next';
import { useGetBranches } from '../services/getBranches';
import { IBranchList } from '../types/Branch';
import { Card } from '../../../components/ui/card';
import { ClockIcon } from '../../../assets/Clock';
import { formatPhoneNumber } from '../../../utils/formatPhone';
import { Separator } from '../../../components/ui/separator';
import { PhoneIcon } from '../../../assets/Phone';

import { BackButtonHide } from '../../../utils/BackButton';
import { cn } from '../../../lib/utils';
import { notificationOccurred } from '../../../utils/hapticEvents';

import {
  LocationResult,
  requestTelegramLocation,
} from '../../../lib/TelegramLocation';
import { getDistanceInMeters } from '../../../utils/distance';

// Hoisted for the same reason as elsewhere: a component type declared inside
// the render body is remounted on every render, re-running setView.
const MapUpdater = ({
  center,
  zoom,
}: {
  center: LatLngExpression;
  zoom: number;
}) => {
  const map = useMap();

  useEffect(() => {
    map.setView(center, zoom);
  }, [center, zoom, map]);

  return null;
};

const DEFAULT_ZOOM = 16;
// «Найти ближайший»: на 17-м уровне подпись и адрес филиала в шторке уже
// сопоставимы с тем, что видно на карте — квартал, а не район.
const NEAREST_BRANCH_ZOOM = 17;

// Свёрнутое положение шторки филиала подгоняем под её контент: с долей
// экрана (раньше 0.45) под «Как добраться» оставалась пустая полоса.
// Раскрытое положение — доля, там контент скроллится.
const EXPANDED_SNAP = 0.9;
// Высота «палочки» сверху шторки (mt-2 + h-1.5) — в контент не входит.
const DRAWER_HANDLE_HEIGHT = 14;

export const Restaurant: React.FC = () => {
  const [collapsedHeight, setCollapsedHeight] = useState(0);
  const snapPoints = useMemo(
    () => [
      collapsedHeight ? `${collapsedHeight + DRAWER_HANDLE_HEIGHT}px` : 0.45,
      EXPANDED_SNAP,
    ],
    [collapsedHeight]
  );
  const [snap, setSnap] = useState<number | string | null>(snapPoints[0]);
  const snapRef = useRef(snap);
  snapRef.current = snap;

  // Меряем контент шторки, пока она свёрнута: в раскрытом виде в нём
  // появляются услуги и баннеры, и это уже не та высота. Callback-ref, а не
  // useEffect: контент живёт в портале и монтируется только с открытием.
  const contentObserver = useRef<ResizeObserver>();
  const measureCollapsed = useCallback((node: HTMLDivElement | null) => {
    contentObserver.current?.disconnect();
    if (!node) return;

    contentObserver.current = new ResizeObserver(([entry]) => {
      if (snapRef.current === EXPANDED_SNAP) return;
      setCollapsedHeight(Math.ceil(entry.contentRect.height));
    });
    contentObserver.current.observe(node);
  }, []);

  // Точки привязки поменялись — свёрнутая шторка должна встать на новую.
  useEffect(() => {
    if (snapRef.current !== EXPANDED_SNAP) setSnap(snapPoints[0]);
  }, [snapPoints]);
  BackButtonHide();
  // useAskTelegramLocation();
  const { latitude: lat, longitude: lng, hasFix } = useSelector(userLocation);
  const [filterOpen, setFilterOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [activeBranch, setActiveBranch] = useState<IBranchList>();
  const { branches } = useGetBranches(lat ?? 41.2995, lng ?? 69.2401);

  const locationUser: [number, number] = [lat ?? 41.2995, lng ?? 69.2401];
  const [mapCenter, setMapCenter] = useState<LatLngExpression>(locationUser);
  const [mapZoom, setMapZoom] = useState(DEFAULT_ZOOM);

  // Ближайший считаем на клиенте по расстоянию, как на карте самовывоза.
  // Раньше брали `/branch/nearby` — это зона доставки, а не ресторан: его
  // `id` не совпадал с `branch.id`, и кнопка молча ничего не делала.
  const focusNearestBranch = (from: LocationResult) => {
    if (!branches.length) return;

    const nearest = branches.reduce((closest, branch) => {
      const distance = getDistanceInMeters(
        from.latitude,
        from.longitude,
        Number(branch.lat),
        Number(branch.long)
      );
      const closestDistance = getDistanceInMeters(
        from.latitude,
        from.longitude,
        Number(closest.lat),
        Number(closest.long)
      );

      return distance < closestDistance ? branch : closest;
    }, branches[0]);

    setMapCenter([Number(nearest.lat), Number(nearest.long)]);
    setMapZoom(NEAREST_BRANCH_ZOOM);
    setActiveBranch(nearest);
    setIsDrawerOpen(true);
    notificationOccurred('success');
  };

  const handleFindBranch = () => {
    if (hasFix) {
      focusNearestBranch({ latitude: lat, longitude: lng });
      return;
    }

    // Без фикса координаты в сторе — центр Ташкента, и «ближайший» к нему
    // филиал не имеет отношения к пользователю. Сначала просим геолокацию;
    // если её не дали (отказ, выключенный GPS) — говорим об этом и карту не
    // трогаем, чтобы не выдавать случайный ресторан за ближайший.
    requestTelegramLocation(focusNearestBranch, () => {
      notificationOccurred('error');
      window.Telegram?.WebApp?.showAlert?.(t('find-near-no-location'));
    });
  };

  const branchMarkerIcon = useMemo(
    () =>
      L.divIcon({
        className: '',
        html: '<span class="block h-5 w-5 rounded-full border-4 border-[#007854] bg-white"></span>',
        iconSize: [20, 20],
        iconAnchor: [10, 20],
      }),
    []
  );

  const userMarkerIcon = useMemo(
    () =>
      L.icon({
        iconUrl: '/assets/pin.png',
        iconSize: [40, 40],
        iconAnchor: [20, 40],
      }),
    []
  );

  return (
    // Fixed, not absolute: vaul sets `position: fixed` on <body> while a
    // drawer is open, which would otherwise collapse this to zero height.
    <div style={{ position: 'fixed', top: 0, bottom: 0, left: 0, right: 0 }}>
      <MapContainer
        center={mapCenter}
        zoom={mapZoom}
        style={{ height: '100%', width: '100%', zIndex: 1 }}
      >
        <TileLayer
          attribution="&copy; OpenStreetMap contributors"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapUpdater center={mapCenter} zoom={mapZoom} />
        {branches.map(branch => (
          <Marker
            key={branch.id}
            position={[Number(branch.lat), Number(branch.long)]}
            icon={branchMarkerIcon}
            eventHandlers={{
              click: () => {
                setMapCenter([Number(branch.lat), Number(branch.long)]);
                setMapZoom(NEAREST_BRANCH_ZOOM);
                setActiveBranch(branch);
                setIsDrawerOpen(branch.id !== activeBranch?.id);
              },
            }}
          />
        ))}
        {hasFix && <Marker position={locationUser} icon={userMarkerIcon} />}
      </MapContainer>
      {/* <div className="absolute  right-0 top-24  flex   justify-end items-end  px-2 ">
        <Button
          className="h-10 bg-white  rounded-full p-3 flex items-center flex-row  gap-x-[6px]"
          onClick={() => navigate('/restaurants/list')}
        >
          <ListIcon className="text-text-icon-secondary" />
          <p className="text-display-sm text-text-tertiary">{t('list')}</p>
        </Button>
      </div> */}
      <div className="fixed bottom-safe-bottom right-0 z-10 flex space-x-2 justify-end items-end px-4">
        <Button
          className="flex-1 h-12 bg-white min-w-12 rounded-full active:scale-95 transition-transform duration-150 active:animate-press-pulse text-text-icon-tertiary active:text-text-white focus:text-text-white focus:animate-press-pulse focus:bg-brand-green active:bg-brand-green"
          onClick={() => {
            requestTelegramLocation();
            setMapCenter([lat ?? 41.2995, lng ?? 69.2401]);
            notificationOccurred('success');
          }}
        >
          <ArrowIcon />
        </Button>
      </div>
      <div className="fixed bottom-safe-bottom z-10 flex space-x-2 justify-center px-4">
        {/* <Button
          className="flex-1  h-12 bg-white min-w-[60px] rounded-xl "
          onClick={() => setFilterOpen(true)}
        >
          <FilterIcon className="text-text-icon-primary" />
        </Button> */}
        <Button
          onClick={handleFindBranch}
          className="flex-1  w-full h-12 min-w-[275px] bg-button-primary rounded-xl flex flex-row items-center justify-center space-x-1"
        >
          <LocationFooterIcon className="h-6 w-6" />
          <p className="text-display-sm">{t('find-near')}</p> 
        </Button>
      </div>
      <Drawer open={filterOpen} onOpenChange={setFilterOpen}>
        <DrawerContent className="bg-fill-background px-4  ">
          <div className="flex flex-col sapce-y-2">
            <div className="flex flex-row justify-between items-center">
              <DrawerTitle>
                <p className="text-display-md ">{t('filters')}</p>
              </DrawerTitle>
              <div
                className="flex items-center justify-center h-6 w-6 rounded-full bg-button-secondary"
                onClick={() => setFilterOpen(false)}
              >
                <XIcon className="text-text-icon-secondary" />
              </div>
            </div>
            <div className=""></div>
            <div className="flex flex-col space-y-2 pb-6  ">
              <Button className="w-full h-12 bg-button-primary rounded-xl flex flex-row items-center justify-center space-x-1">
                {t('show-results')}
              </Button>
              <Button className="w-full h-12 bg-button-primary rounded-xl flex flex-row items-center justify-center space-x-1">
                {t('reset-filters')}
              </Button>
            </div>
          </div>
        </DrawerContent>
      </Drawer>
      <Drawer
        shouldScaleBackground={false}
        open={isDrawerOpen}
        snapPoints={snapPoints}
        activeSnapPoint={snap}
        setActiveSnapPoint={setSnap}
        onOpenChange={(open: boolean) => {
          setIsDrawerOpen(open);
          setActiveBranch(undefined);
        }}
      >
        {/* <DrawerOverlay className="bg-fill-background inset-0 fixed"> */}
        {/* <DrawerPortal> */}
        <DrawerContent
          overlayClassName="bg-transparent"
          className="bg-fill-background px-4  h-full"
        >
          <div
            ref={measureCollapsed}
            className="flex flex-col space-y-2 bg-fill-background"
          >
            <div className="flex flex-row justify-between items-center">
              <DrawerTitle>
                <p className="text-display-md  ">{activeBranch?.title}</p>
              </DrawerTitle>
              <div
                className="flex items-center justify-center h-6 w-6 rounded-full bg-button-secondary"
                onClick={() => {
                  setIsDrawerOpen(false);
                  setActiveBranch(undefined);
                }}
              >
                <XIcon className="text-text-icon-secondary" />
              </div>
            </div>
            <p className="text-body-sm-regular text-text-secondary">
              {activeBranch?.address}
            </p>
            <Card className="flex flex-col pl-[18px] py-[9px] rounded-xl  ">
              <div className="flex flex-col">
                <div className="flex flex-row items-center space-x-[13px]">
                  <PhoneIcon className="text-text-icon-secondary" />
                  <div className="flex flex-col">
                    <p className="text-body-base-regular ">{t('phone')}</p>
                    <p className="text-body-sm-regular text-text-tertiary">
                      {formatPhoneNumber(activeBranch?.phone ?? '')}
                    </p>
                  </div>
                </div>
                <div className="pl-7 py-[9px]">
                  <Separator />
                </div>
              </div>
              <div className="flex flex-col">
                <div className="flex flex-row items-center space-x-[13px]">
                  <ClockIcon className="text-text-icon-secondary" />
                  <div className="flex flex-col">
                    <p className="text-body-base-regular ">{t('work-time')}</p>
                    <p className="text-body-sm-regular text-text-tertiary">
                      {activeBranch?.working_hours}
                    </p>
                  </div>
                </div>
              </div>
            </Card>
            {snap === EXPANDED_SNAP && (
              <div
                className={cn('py-2 flex flex-col space-y-4 transition-all', {
                  'opacity-100 translate-y-0': snap === EXPANDED_SNAP,
                  'opacity-0 translate-y-2': snap !== EXPANDED_SNAP,
                })}
              >
                <p className="text-display-md">{t('restaurant.services')}</p>
                <div className="flex flex-row flex-wrap">
                  {activeBranch?.tags.map(tag => (
                    <div
                      key={tag.id}
                      className="flex flex-row items-center space-x-2 mr-3 mb-2 py-2 px-3 bg-white rounded-xl"
                    >
                      <p className="text-body-sm-medium text-text-primary">
                        {tag.title}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {snap === EXPANDED_SNAP && (
              <div
                className={cn('flex flex-col space-y-4 transition-all', {
                  'opacity-100 translate-y-0': snap === EXPANDED_SNAP,
                  'opacity-0 translate-y-2': snap !== EXPANDED_SNAP,
                })}
              >
                <p className="text-display-md">{t('restaurant.banner')}</p>
                <div className="flex flex-row overflow-y-auto gap-x-2 ">
                  {activeBranch?.banners.map(banner => (
                    <img
                      key={banner.id}
                      src={banner.image}
                      className="h-[122px] object-cover rounded-xl"
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-col space-y-2 pb-6 pt-2 w-full">
              {/* Самовывоз отключён — «Заказать отсюда» вернуть вместе с ним.
                  Распорка на её месте убрана: пустой блок читался как
                  неотрисовавшаяся кнопка. */}
              {/* <Button className="w-full h-12 bg-button-primary rounded-xl flex flex-row items-center justify-center space-x-1">
                <p className="text-display-sm">{t('order-here')}</p>
              </Button> */}
              <Button
                onClick={() =>
                  window.Telegram.WebApp.openLink(
                    `https://yandex.uz/maps/10335/tashkent/?mode=routes&rtext=${locationUser.join(',')}~${activeBranch?.lat},${activeBranch?.long}&rtt=auto&ruri=~&z=12`
                  )
                }
                className="w-full h-12 bg-button-secondary rounded-xl flex flex-row items-center justify-center space-x-1"
              >
                <LocationFooterIcon className="text-text-icon-primary h-6 w-6" />
                <p className="text-display-sm text-text-primary">
                  {t('how-to-get')}
                </p>
              </Button>
            </div>
          </div>
        </DrawerContent>
        {/* </DrawerPortal> */}
        {/* </DrawerOverlay> */}
      </Drawer>
    </div>
  );
};

export default Restaurant;
